import type { Editor } from '@tiptap/react';

export const FONT_FAMILIES = ['Helvetica', 'Arial', 'Times New Roman', 'Courier New', 'Georgia'];
export const FONT_SIZES = ['10', '12', '14', '16', '18', '20', '24', '28', '32'];

const unquote = (value: string) => value.replace(/["']/g, '').trim();

/**
 * Map a font value stored in the document onto the dropdown's own spelling.
 *
 * Stored values are not guaranteed to be byte-identical to a list entry: a
 * preset can hold `arial`, and pasted content arrives as a full stack like
 * `Arial, sans-serif`. Mantine's Select falls back to its placeholder for any
 * value it cannot find in `data`, and this toolbar's placeholder is the word
 * "Helvetica" — which is how text genuinely set in Arial came to display as
 * Helvetica.
 *
 * An unrecognised family is returned as-is rather than dropped, so the author
 * sees what the text actually uses instead of a blank control.
 */
export function matchFontFamily(stored: string | null | undefined, options = FONT_FAMILIES): string | null {
  if (!stored) return null;
  const first = unquote(stored.split(',')[0] ?? '');
  if (!first) return null;
  return options.find((option) => option.toLowerCase() === first.toLowerCase()) ?? first;
}

/** `'14px'` / `'14'` -> `'14'`, matching the size dropdown's unitless entries. */
export function matchFontSize(stored: string | null | undefined, options = FONT_SIZES): string | null {
  if (!stored) return null;
  const value = unquote(String(stored)).replace(/px$/i, '');
  if (!value) return null;
  return options.find((option) => option === value) ?? value;
}

/**
 * Widen a dropdown's options with the current value when it sits outside the
 * list, so a font or size the document already uses stays visible and
 * selectable instead of rendering as an empty control.
 */
export function withCurrentValue(current: string | null, options: string[]): string[] {
  if (!current || options.includes(current)) return options;
  return [...options, current];
}

/**
 * Apply text-style marks, covering the caret's whole block when nothing is
 * selected.
 *
 * TipTap's `setMark` on an empty selection only records stored marks, which
 * apply to text typed next. The author, having selected nothing and picked a
 * font, sees the paragraph in front of them stay exactly as it was while the
 * toolbar claims the font changed. Selecting the block first makes the control
 * do what it says; the caret is restored afterwards so typing continues where
 * it left off.
 */
export function applyTextStyle(editor: Editor, attrs: Record<string, string>) {
  const { empty, from } = editor.state.selection;

  if (!empty) {
    editor.chain().focus().setMark('textStyle', attrs).run();
    return;
  }

  const { $from } = editor.state.selection;
  editor
    .chain()
    .focus()
    .setTextSelection({ from: $from.start(), to: $from.end() })
    .setMark('textStyle', attrs)
    .setTextSelection(from)
    .run();
}

/**
 * The font family and size in force at the selection.
 *
 * Read from the document rather than tracked in component state: state seeded
 * at `null` and only written by the dropdowns themselves cannot reflect content
 * loaded from a saved flow, or a callout inserted by a preset, so the controls
 * described whatever the author last clicked instead of what the text is.
 */
export function currentTextStyle(editor: Editor | null) {
  if (!editor) return { fontFamily: null, fontSize: null };

  const textStyle = editor.getAttributes('textStyle');
  // Callout text carries its font on the node, not on a textStyle mark.
  const callout = editor.isActive('calloutBox') ? editor.getAttributes('calloutBox') : {};

  return {
    fontFamily: (textStyle.fontFamily ?? callout.fontFamily ?? null) as string | null,
    fontSize: (textStyle.fontSize ?? callout.fontSize ?? null) as string | null,
  };
}
